import { AuthenticationService } from '../authentication.service';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EventSourcePolyfill } from 'event-source-polyfill';


export abstract class StreamService {
  // Map to store multiple event sources, each with a unique identifier
  private eventSources = new Map<string, EventSourcePolyfill>();

  protected constructor(
    protected http: HttpClient,
    protected authenticationService: AuthenticationService
  ) {

  }

  public abstract servicePath(): string;

  /// ///////////////////////////////////////////////////////////////////////////
  // Public methods
  eventSourceStream(path: string, params: HttpParams): Observable<any> {
    const headers = {
      'Authorization': 'Bearer ' + this.authToken
    };

    const url = this.resolvePath(path) + '?' + params.toString();

    // Extract source and uniqueId from params if available
    const sourceParams = params.get('source') || 'unknown';
    const uniqueId = params.get('uniqueId') || `stream-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const streamId = `${sourceParams}-${uniqueId}`;

    return new Observable(observer => {
      console.log(`Creating new EventSource for ${sourceParams} with ID: ${streamId}`);

      // Check if this stream already exists, if so, close it
      if (this.eventSources.has(streamId)) {
        console.log(`Closing existing EventSource for ${streamId}`);
        this.closeEventSource(streamId);
      }

      // Create a new EventSource for this stream
      const eventSource = new EventSourcePolyfill(url, { headers, heartbeatTimeout: 300000 });

      // Store it in the map
      this.eventSources.set(streamId, eventSource);

      eventSource.onmessage = (event) => {
        // Check for a special completion marker sent by the server
        // This allows the server to signal when the stream is complete
        if (event.data && typeof event.data === 'string' && event.data.includes('"complete":true')) {
          console.log(`Received complete marker for ${streamId}`);
          observer.next(event.data); // Pass the final data
          observer.complete();
          this.closeEventSource(streamId);
          return;
        }

        observer.next(event.data);
      };

      eventSource.onerror = (error) => {
        console.error(`EventSource error for ${streamId}:`, error);
        observer.error(error);
        this.closeEventSource(streamId);
      };

      // Add an explicit event handler for stream completion
      eventSource.addEventListener('complete', () => {
        console.log(`EventSource complete event for ${streamId}`);
        observer.complete();
        this.closeEventSource(streamId);
      });

      // Return a cleanup function for when the Observable is unsubscribed
      return () => {
        console.log(`Unsubscribing from EventSource for ${streamId}`);
        this.closeEventSource(streamId);
      };
    });
  }

  // Helper method to close an EventSource and remove it from the map
  private closeEventSource(streamId: string): void {
    const eventSource = this.eventSources.get(streamId);
    if (eventSource) {
      console.log(`Closing EventSource with ID: ${streamId}`);
      eventSource.close();
      this.eventSources.delete(streamId);
    }
  }

  // Method to close all active EventSources
  protected closeAllEventSources(): void {
    console.log(`Closing all EventSources (total: ${this.eventSources.size})`);
    this.eventSources.forEach((eventSource, id) => {
      console.log(`Closing EventSource: ${id}`);
      eventSource.close();
    });
    this.eventSources.clear();
  }

  /// ///////////////////////////////////////////////////////////////////////////
  // Private methods

  protected get authToken(): string {
    return this.authenticationService.getTokenInfo().accessToken;
  }

  protected resolvePath(path: string): string {
    // Remove leading and trailing slashes
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    if (path.endsWith('/')) {
      path = path.substring(0, path.length - 1);
    }

    // Remove leading and trailing slashes from service path
    if (this.servicePath().endsWith('/')) {
      path = this.servicePath().concat(path);
    } else {
      path = this.servicePath().concat('/', path);
    }
    return path;
  }
}
